import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useReveal } from "@/hooks/useReveal";
import { Instagram, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const ref = useReveal();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast("Por favor preencha nome, email e mensagem.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast("Mensagem enviada · Em breve responderemos.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast("Não foi possível enviar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contactos"
      ref={ref}
      className="relative bg-[var(--luft-bg-alt)] py-32 sm:py-44 lg:py-52"
      data-testid="contact-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="mb-24 reveal">
          <p className="eyebrow mb-8">10 — Contactos</p>
          <h2 className="editorial-heading text-[clamp(2.2rem,5.4vw,4.4rem)] text-[var(--luft-ink)] max-w-4xl">
            Diga <em className="editorial-italic">olá</em>.<br />
            Estamos perto.
          </h2>
        </div>

        <div className="grid grid-cols-12 gap-y-16 lg:gap-x-14">
          {/* Left: Info */}
          <div className="col-span-12 lg:col-span-5 reveal">
            <div className="space-y-12">
              <div>
                <p className="eyebrow mb-4">Telefone</p>
                <a
                  href="tel:+351912952121"
                  className="link-underline font-display text-2xl text-[var(--luft-ink)]"
                  data-testid="contact-phone"
                >
                  +351 912 952 121
                </a>
              </div>

              <div>
                <p className="eyebrow mb-4">Email</p>
                <a
                  href="mailto:geral@luftpilates.com"
                  className="link-underline font-display text-2xl text-[var(--luft-ink)]"
                  data-testid="contact-email"
                >
                  geral@luftpilates.com
                </a>
              </div>

              <div>
                <p className="eyebrow mb-4">Instagram</p>
                <a
                  href="https://instagram.com/lu.ft_pilates"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 font-display text-2xl text-[var(--luft-ink)] link-underline"
                  data-testid="contact-instagram"
                >
                  <Instagram size={18} strokeWidth={1.4} />
                  @lu.ft_pilates
                </a>
              </div>

              <div>
                <p className="eyebrow mb-4">Linktree</p>
                <a
                  href="https://linktr.ee/pilates.luft"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-underline font-display text-xl text-[var(--luft-ink)]"
                  data-testid="contact-linktree"
                >
                  linktr.ee/pilates.luft
                </a>
              </div>

              <div>
                <p className="eyebrow mb-4">Horário</p>
                <p className="font-body font-light text-[var(--luft-ink-soft)] leading-relaxed">
                  Aulas por marcação prévia.<br />
                  Encerrado ao domingo.
                </p>
              </div>

              <div>
                <p className="eyebrow mb-4">Localizações</p>
                <button
                  onClick={() =>
                    document.getElementById("estudios")?.scrollIntoView({ behavior: "smooth" })
                  }
                  className="link-underline font-body font-light text-[var(--luft-ink)] text-sm"
                  data-testid="contact-studios-link"
                >
                  Ver os dois estúdios em Entroncamento
                </button>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="col-span-12 lg:col-span-7 lg:pl-12 reveal">
            <form onSubmit={submit} className="space-y-2" data-testid="contact-form">
              <p className="eyebrow mb-10">Enviar mensagem</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10">
                <div>
                  <label className="eyebrow text-[var(--luft-muted)]" htmlFor="name">Nome</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={onChange}
                    placeholder="O seu nome"
                    className="input-naked"
                    data-testid="contact-input-name"
                    required
                  />
                </div>
                <div>
                  <label className="eyebrow text-[var(--luft-muted)]" htmlFor="email">Email</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="ola@email.com"
                    className="input-naked"
                    data-testid="contact-input-email"
                    required
                  />
                </div>
              </div>

              <div className="pt-6">
                <label className="eyebrow text-[var(--luft-muted)]" htmlFor="phone">Telefone (opcional)</label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={onChange}
                  placeholder="+351 ..."
                  className="input-naked"
                  data-testid="contact-input-phone"
                />
              </div>

              <div className="pt-6">
                <label className="eyebrow text-[var(--luft-muted)]" htmlFor="message">Mensagem</label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  value={form.message}
                  onChange={onChange}
                  placeholder="Conte-nos um pouco sobre si, e em que podemos ajudar."
                  className="input-naked"
                  data-testid="contact-input-message"
                  required
                />
              </div>

              <div className="pt-12">
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-ink disabled:opacity-60"
                  data-testid="contact-submit-btn"
                >
                  {loading ? "A enviar..." : "Enviar mensagem"}
                  <ArrowRight size={14} strokeWidth={1.4} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
